import { Component, OnInit } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { BaseComponent } from '@core/components/base-component';
import { EnumItem } from '@core/models/enum-item.model';
import { CurrencyRateOperationEnum } from '@enums/CurrencyRateOperation';
import { EstimateTypeEnum } from '@enums/EstimateType';
import { StatusEnum } from '@enums/Status';
import { EstimateBudgetTypes } from '@estimate/estimate-budget-types/models/estimate-budget-types.model';
import { Company } from '@interfaces/Company';
import { CompanyCurrency } from '@interfaces/CompanyCurrency';
import { CustomerDto } from '@interfaces/CustomerDto';
import { Estimate } from '@interfaces/Estimate';
import { EstimateConfiguration } from '@interfaces/EstimateConfiguration';
import { InternalResourceGroup } from '@interfaces/InternalResourceGroup';
import { Resource } from '@interfaces/Resource';
import { CurrencyRateOperationEnumItems } from '@models/consts/currency-rate-operation-enum-items.const';
import { EstimateCrmSyncItems } from '@models/consts/estimate-crm-sync-items.const';
import { TranslateService } from '@ngx-translate/core';
import { AppSettingsService } from '@shared/services/app-settings.service';
import { CurrencyService } from '@shared/services/currency.service';
import { EstimateBudgetService } from '@shared/services/estimate-budget.service';
import { EstimateSettingsService } from '@shared/services/estimate-settings.service';
import { EstimateService } from '@shared/services/estimate.service';
import { LoadingService } from '@shared/services/loading.service';
import { UserService } from '@shared/services/user.service';
import { ConfirmationService } from 'primeng/api';
import { Observable, combineLatest, filter, firstValueFrom, map, switchMap, take } from 'rxjs';

@Component({
  selector: 'pop-properties-modal',
  templateUrl: './properties-modal.component.html',
  styleUrls: ['./properties-modal.component.less']
})
export class PropertiesModalComponent extends BaseComponent implements OnInit {
  public canUpdateCurrencyRate = false;
  public companies: Company[];
  public currencies$: Observable<CompanyCurrency[]>;
  public currencyOperationValues: EnumItem<CurrencyRateOperationEnum>[];
  public currentEstimate: Estimate;
  public customers?: CustomerDto[];
  private departmentLabel: string;
  public estimateBudgetTypes?: EstimateBudgetTypes;
  private fieldsToSync: string[];
  public expenseBillingMethodCodes$: Observable<string[]>;
  public fixedMaxBudgeted: boolean;
  public forceLaborBillingMethod: boolean;
  public hasCRM: boolean;
  public internalFeeBillingMethodCodes$: Observable<string[]>;
  public internalResourceGroups: InternalResourceGroup[];
  public isEstimateReadonly: boolean;
  public isFunctionalCurrency: boolean;
  public isGroupApproved?: boolean;
  public isStatusTypeNew?: boolean;
  public isEstimateTypeNew?: boolean;
  public laborBillingMethodCodes$: Observable<string[]>;
  private newCurrency?: CompanyCurrency;
  public projectCreatedByPop = false;
  public projectTemplates?: string[];
  public resources?: Resource[];
  public settings: EstimateConfiguration;
  public showSections: Record<string, boolean> = {
    information: true,
    budgetType: false,
    currency: false,
    billingMethods: false
  };

  public constructor(
    private estimateService: EstimateService,
    private router: Router,
    private confirmationService: ConfirmationService,
    private loadingService: LoadingService,
    private translate: TranslateService,
    private snackBar: MatSnackBar,
    private currencyService: CurrencyService,
    private estimateSettings: EstimateSettingsService,
    private appSettingsService: AppSettingsService,
    private userService: UserService,
    public estimateBudgetService: EstimateBudgetService
  ) {
    super();
    this.currencyOperationValues = CurrencyRateOperationEnumItems;
    this.projectCreatedByPop = this.estimateBudgetService.hasApprovedEstimate;
  }

  public async ngOnInit() {
    this.subscribe(
      combineLatest([
        this.estimateService.observeCurrentEstimate(),
        this.estimateService.observeIsCurrentEstimateGroupApproved()
      ])
        .pipe(
          switchMap(([estimate, isGroupApproved]) => {
            this.currentEstimate = estimate;
            if (!this.estimateBudgetTypes?.matchesEstimate(estimate)) {
              this.estimateBudgetTypes = new EstimateBudgetTypes(estimate);
            }

            this.isGroupApproved = isGroupApproved;
            this.isStatusTypeNew = estimate.status === StatusEnum.New;
            this.isEstimateTypeNew = estimate.estimateType === EstimateTypeEnum.New;
            this.isEstimateReadonly = !this.isStatusTypeNew || this.isGroupApproved || !this.isEstimateTypeNew;

            return this.currencyService.getFunctionalCurrency(this.currentEstimate.companyCode);
          })
        )
        .subscribe(functionalCurrency => {
          this.isFunctionalCurrency = this.currentEstimate.currencyCode === functionalCurrency.currencyCode;
        })
    );

    this.settings = await this.estimateSettings.getEstimateConfigurationAsync(false);
    this.companies = await this.estimateService.getCompaniesAsync();
    this.hasCRM = (await this.appSettingsService.getAppSettingsAsync()).hasCRM;
    this.fixedMaxBudgeted = this.settings.forceMaxBudgeted;
    this.forceLaborBillingMethod = this.settings.forceLaborBillingMethod;
    const user = await this.userService.getUserAsync();
    const restrictedCompanies = this.settings.restrictedCompanies.split(',');
    this.resources = await this.estimateService.getResources(user, restrictedCompanies);
    this.laborBillingMethodCodes$ = this.estimateService.getLaborBillingMethodCodes();
    this.expenseBillingMethodCodes$ = this.estimateService.getExpenseBillingMethodCodes();
    this.internalFeeBillingMethodCodes$ = this.estimateService.getInternalFeeBillingMethodCodes();

    await firstValueFrom(this.estimateService.observeCurrentEstimate());

    this.currencies$ = this.currencyService
      .getCompanyCurrencies()
      .pipe(map(o => o.filter(currency => currency.companyCode === this.currentEstimate.companyCode)));
    this.updateCurrency(this.currentEstimate);
    const templates = await this.estimateService.getProjectTemplatesAsync();
    this.projectTemplates = templates.map(t => t.code);
    this.customers = await this.estimateService.getCustomerByCompanyAsync(this.currentEstimate.companyCode);
    this.departmentLabel = await this.estimateSettings.getDepartmentLabel();
    this.internalResourceGroups = await this.estimateService.getInternalResourceGroups();
  }

  private updateCurrency(estimate: Estimate) {
    const { companyCode, currencyCode, status } = estimate;
    if (companyCode && currencyCode && status === StatusEnum.New) {
      this.subscribe(
        this.currencyService
          .getCurrencyByCompanyAndCode(companyCode, currencyCode)
          .pipe(take(1), filter(Boolean))
          .subscribe(curr => {
            this.newCurrency = curr;
            const operationHasChanged = estimate.currencyRateOperation != curr.operation;
            const rateHasChanged = curr.rate !== estimate.currencyRate;

            this.canUpdateCurrencyRate = !curr.isFunctional && (operationHasChanged || rateHasChanged);
          })
      );
    }
  }

  public get currentEstimateRate(): number {
    return this.currentEstimate.rate;
  }
  public set currentEstimateRate(rate: number) {
    rate = Number(rate);
    if (!isNaN(rate) && rate >= 0) {
      this.currentEstimate.rate = Number(rate.toFixed(2));
    }
  }

  public confirmMethod(newMethodCode: string) {
    const currentMethodCode = this.currentEstimate.laborBillingMethodCode;
    this.confirmationService.confirm({
      message: this.translate.instant('ERROR.METHOD') as string,
      header: this.translate.instant('HEADER.METHOD') as string,
      icon: 'fas fa-exclamation-triangle',
      key: 'global-confirmation-dialog',
      acceptLabel: this.translate.instant('BUTTON.CONFIRM') as string,
      rejectLabel: this.translate.instant('BUTTON.CANCEL') as string,
      accept: () => {
        this.setLaborBillingMethodCode(newMethodCode);
      },
      reject: () => {
        let index: string;
        const elem: any = document.getElementById('laborBillingMethodCode');
        for (const option of elem) {
          const label = option.label.trim();
          if (currentMethodCode === label) {
            index = option.index;
          }
        }
        elem.value = index + ': ' + currentMethodCode;
        this.setLaborBillingMethodCode(currentMethodCode);
      }
    });
  }

  public openDeleteDialog() {
    this.confirmationService.confirm({
      message: this.translate.instant('ERROR.DELETE_EST') as string,
      header: this.translate.instant('HEADER.DELETE') as string,
      icon: 'fas fa-exclamation-circle',
      key: 'de',
      acceptLabel: this.translate.instant('BUTTON.DELETE') as string,
      rejectLabel: this.translate.instant('BUTTON.CANCEL') as string,
      accept: () => {
        this.estimateService.deleteEstimate(this.currentEstimate.id).subscribe(res => {
          if (res != -1) {
            this.router.navigate(['/app/estimate-list']);
          } else {
            this.openSnackBar();
          }
        });
      },
      reject: () => {
        const elem = document.getElementById('confirm-delete') as HTMLInputElement;
        elem.checked = false;
      }
    });
  }

  private openSnackBar() {
    this.snackBar.open(this.translate.instant('ERROR.DELETE') as string, '', {
      duration: 3500
    });
  }

  public syncWithCrm() {
    this.fieldsToSync = [];
    this.estimateService.getFieldsToSyncWithCrm().subscribe(results => {
      this.fieldsToSync = results;
      const noChanges = this.fieldsToSync.length === 0;
      const confirmSyncMessage = this.getSyncMsg(this.fieldsToSync);
      this.confirmSyncWithCrm(confirmSyncMessage, noChanges);
    });
  }

  private getSyncMsg(fieldsToSync: string[]) {
    if (fieldsToSync.length === 0) {
      return this.translate.instant('SYNC_CRM.NO_CHANGES') as string;
    }

    let confirmSyncMessage = '';
    let projectModelHasChanged = false;
    let concatFieldMsgs = '';

    for (const field of fieldsToSync) {
      let departmentChanged = false;
      const fieldItem = EstimateCrmSyncItems.find(e => e.key === field);

      if (fieldItem.key === 'projectModelCode') {
        projectModelHasChanged = true;
      }

      if (fieldItem.key === 'departmentCode') {
        departmentChanged = true;
      }

      const fieldMsg = departmentChanged
        ? `[${this.departmentLabel}]`
        : `${this.translate.instant(fieldItem.labelKey) as string}`;

      concatFieldMsgs += `\u2022 ${fieldMsg} \n\n`;
    }

    confirmSyncMessage = `${this.translate.instant('SYNC_CRM.CHANGES') as string} \n\n${concatFieldMsgs}`;

    if (projectModelHasChanged) {
      if (!this.isStatusTypeNew) {
        confirmSyncMessage = this.translate.instant('SYNC_CRM.PROJECT_TEMPLATE_CANNOT_SYNC') as string;
      } else {
        confirmSyncMessage += `${this.translate.instant('SYNC_CRM.PROJECT_TEMPLATE_WARN') as string}`;
      }
    }

    return confirmSyncMessage;
  }

  private confirmSyncWithCrm(syncMsg: string, noChanges: boolean) {
    this.confirmationService.confirm({
      message: syncMsg,
      header: this.translate.instant('SYNC_CRM.BUTTON') as string,
      icon: 'fas fa-exclamation-triangle',
      key: 'global-confirmation-dialog',
      acceptLabel: noChanges ? '' : (this.translate.instant('BUTTON.CONFIRM') as string),
      rejectLabel: noChanges
        ? (this.translate.instant('BUTTON.OK') as string)
        : (this.translate.instant('BUTTON.CANCEL') as string),
      accept: () => {
        this.estimateService.synchronizeEstimateWithCrm().subscribe(() => {
          this.estimateService.dialogEmitChanges();
          this.estimateService.saveChangesAsync();
        });
      },
      reject: () => {}
    });
  }

  public setCurrencyOperation(event: CurrencyRateOperationEnum) {
    if (this.currentEstimate.currencyRateOperation === event) {
      return;
    }
    this.currentEstimate.currencyRateOperation = event;
    this.estimateService.saveChangesAsync();
  }

  private async onBudgetTypeChangeAsync() {
    const desiredEstimateBudgetTypes = this.estimateBudgetTypes;
    if (this.estimateService.entityManagerHasChanges()) {
      await this.estimateService.saveChangesAsync();
    }
    await this.estimateService.deleteAllEstimateLines();
    const estimate = this.estimateService.currentEstimate;

    estimate.budgetType = desiredEstimateBudgetTypes.selectedBudgetType;
    estimate.budgetTypeExpense = desiredEstimateBudgetTypes.selectedBudgetTypeExpense;
    estimate.budgetTypeInternalFee = desiredEstimateBudgetTypes.selectedBudgetTypeInternalFee;
    estimate.budgetTypeTime = desiredEstimateBudgetTypes.selectedBudgetTypeTime;

    await this.estimateService.saveChangesAsync();
    await this.estimateService.refreshEstimateAsync();
  }

  public onBudgetTypeChange() {
    if (!this.currentEstimate || this.estimateBudgetTypes.matchesEstimate(this.currentEstimate)) {
      return;
    }

    void this.loadingService.run(async () => this.onBudgetTypeChangeAsync());
  }

  private setLaborBillingMethodCode(laborBillingMethodCode: string) {
    this.currentEstimate.laborBillingMethodCode = laborBillingMethodCode;
    this.estimateService.saveChanges();
  }

  public modifyCurrency(currencyCode: string) {
    this.currencyService.getCurrencySymbol(currencyCode, true).subscribe(result => {
      this.estimateService.saveChanges();
    });
  }

  public updateCurrencyRate() {
    if (this.canUpdateCurrencyRate) {
      this.confirmCurrencyRateUpdate();
    }
  }

  public confirmCurrencyRateUpdate() {
    this.confirmationService.confirm({
      message: this.translate.instant('MODAL.CURRENCY_RATE_UPDATE_MSG', {
        oldRate: this.currentEstimate.currencyRate,
        oldOperation: this.currentEstimate.currencyRateOperation,
        newRate: this.newCurrency.rate,
        newOperation: this.newCurrency.operation
      }) as string,
      header: this.translate.instant('MODAL.CURRENCY_RATE_UPDATE') as string,

      icon: 'fas fa-dollar-sign',
      key: 'global-confirmation-dialog',
      acceptLabel: this.translate.instant('BUTTON.CONFIRM') as string,
      rejectLabel: this.translate.instant('BUTTON.CANCEL') as string,
      accept: () => {
        this.currentEstimate.currencyRate = this.newCurrency.rate;
        this.currentEstimate.currencyRateOperation = this.newCurrency.operation;
        this.estimateService.saveChanges().subscribe(() => {
          this.canUpdateCurrencyRate = false;
        });
      },
      reject: () => {}
    });
  }
}
